import type { AuthResponse, AuthUser, HealthResponse } from './types.js';
import type { LoginInput } from './schemas.js';

/**
 * The single typed entry point to the backend. MyPoz has no API of its own — this talks to an
 * external service whose base URL comes from VITE_API_URL at build time.
 *
 * Add one method per endpoint, grouped by `// --- section ---`. Response types come from
 * types.ts; request bodies are typed by the zod-inferred inputs from schemas.ts.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

export class ApiClient {
  constructor(private opts: ApiClientOptions) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    const token = this.opts.getToken?.();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    // FormData sets its own multipart boundary — never override it.
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const res = await fetch(`${this.opts.baseUrl}${path}`, { ...init, headers });
    if (res.status === 401) this.opts.onUnauthorized?.();

    const text = await res.text();
    const data = text ? JSON.parse(text) : null;

    if (!res.ok) {
      const message = (data && (data.error || data.message)) || res.statusText;
      throw new ApiError(res.status, message, data);
    }
    return data as T;
  }

  private json<T>(path: string, method: string, body?: unknown) {
    return this.request<T>(path, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  // --- system ---

  health() {
    return this.request<HealthResponse>('/api/health');
  }

  // --- auth ---

  login(input: LoginInput) {
    return this.json<AuthResponse>('/api/auth/login', 'POST', input);
  }

  me() {
    return this.request<AuthUser>('/api/auth/me');
  }
}
